import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, CardActions } from '@material-ui/core';
import { classes } from 'typestyle';
import { get, isNil, forEach } from 'lodash';
import { toast } from 'react-toastify';

import container from 'src/ServiceContainer';
import ServiceContainer from 'src/ServiceContainer';
import { errorToLoggingPayload } from 'src/services/loggingService';

import * as modalStyles from 'src/components/TemplateModalWithButton/TemplateModalWithButton.styles';
import { MUIStyles } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/ReceiptsAdjCalculator.styles';
import AcceptButton from 'src/components/AcceptButton/AcceptButton';
import RejectButton from 'src/components/RejectButton/RejectButton';

import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { viewWidth } from 'csx';

import { getGroupedData } from 'src/pages/Hindsighting/StyleColorReview/CanvasView/CanvasView.selectors';
import { retrieveIdentityPropsConfig } from 'src/components/StandardCardView/StandardCardView.utils';

import type { ICellEditorParams } from '@ag-grid-community/core';
import type { BasicPivotItem, ListDataOptions } from 'src/worker/pivotWorker.types';
import type { CanvasViewSlice } from 'src/pages/Hindsighting/StyleColorReview/CanvasView/CanvasView.slice';

import {
  initialGroupBy,
  initialSortBy,
  initialPareDown,
  type SubheaderSlice,
} from 'src/components/Subheader/Subheader.slice';

import type {
  ViewSimilarItemsConfigResponse,
  ViewSimilarItemsModalProps,
  ViewSimiliarItemsSubheaderChange,
} from '../ViewSimilarItems/ViewSimilarItems.types';

import { ViewSimilarItemsSubheader } from '../ViewSimilarItems/ViewSimilarItemsSubheader';
import { ViewsimilarItemsCardsView } from '../ViewSimilarItems/ViewSimilarItemsCardsView';
import { T } from 'lodash/fp';

type ModelApi = {
  modelId: string;
  options: ListDataOptions;
};

type Props = ICellEditorParams & {
  modalParams: ViewSimilarItemsModalProps & {
    updates: { from: string; to: string }[];
    searchBy?: string[];
  };
};

/**
 * Base subheader state copied from the real modal so it behaves the same
 */
const initialState: SubheaderSlice = {
  search: '',
  groupBy: initialGroupBy,
  sortBy: initialSortBy,
  pareDown: initialPareDown,
  subheaderLoading: false,
  lookBackPeriod: '',
  flowStatus: [],
  altSearch: '',
  altFlowStatus: [],
  favorites: [],
  activeFavorite: null,
  possibleBreadcrumbs: null,
  extraPivotParamsSelections: {},
  activeWorkflowKey: null,
  companionSortField: null,
  companionSortDirection: 'desc',
  companionLevelField: null,
};

/**
 * Fetches the card view configs (viewDefn + calcViewDefn + identity config).
 */
async function getCardDefns(cardViewDefns: string[]) {
  const configResponse: ViewSimilarItemsConfigResponse = await container.tenantConfigClient.getTenantViewDefns({
    defnIds: cardViewDefns,
    appName: ASSORTMENT,
  });

  const [viewDefn, calcViewDefn] = configResponse;
  const identityPropsConfig = retrieveIdentityPropsConfig(viewDefn)!;

  return { viewDefn, calcViewDefn, identityPropsConfig };
}

/**
 * Fetches pivot card data using the modelApi defined in the viewDefn
 */
async function fetchCardData(cardConfigs: any, topMembers?: string) {
  if (!cardConfigs) return [];

  const modelReq = get(cardConfigs.viewDefn, 'main.modelApi') as unknown as ModelApi;

  if (topMembers) {
    modelReq.options.topMembers = topMembers;
  }

  const data = await container.pivotService.listData(modelReq.modelId, ASSORTMENT, modelReq.options);

  return data.flat ?? [];
}

/**
 * Determines the topMembers value used by the modal query, if not configured we use `id`
 */
function getTopMembers(data: any, modalParams: ViewSimilarItemsModalProps) {
  if (!isNil(modalParams.topMembers)) {
    return modalParams.topMembers;
  }

  const rowId = get(data, 'id');
  if (!isNil(rowId)) {
    return rowId;
  }
  // shouldn't happen
  return undefined;
}

/**
 * Subheader helper to apply changes without needing to spread in the component
 */
function applySubheaderChanges(prev: SubheaderSlice, changes: ViewSimiliarItemsSubheaderChange) {
  const { search, groupBy, sortBy, groupByDefn, sortByDefn } = changes;

  return {
    ...prev,
    search: !isNil(search) ? search : prev.search,
    groupBy: !isNil(groupBy) ? groupBy : prev.groupBy,
    sortBy: !isNil(sortBy) ? sortBy : prev.sortBy,
    groupByDefn: !isNil(groupByDefn) ? groupByDefn : prev.groupByDefn,
    sortByDefn: !isNil(sortByDefn) ? sortByDefn : prev.sortByDefn,
  };
}

/**
 * This is essentially:
 *   ViewSimilarItemsModal + TemplateModalWithButton
 * but adapted to work as an editor
 *
 * Why this exists:
 * - We cannot use TemplateModalWithButton directly inside an editor, it was
 *   rendering the button and had to click twice to get this to open
 * - Editors mount/unmount instantly on click
 */
export default function ViewSimilarItemsGridEditor(props: Props) {
  const { api, node, data, modalParams } = props;

  /**
   * dialog is open immediately on mount
   * replaces TemplateModalWithButton.open()
   */
  const [open, setOpen] = React.useState(true);

  const [subheaderSlice, setSubheaderSlice] = React.useState<SubheaderSlice>({
    ...initialState,
    search: '',
  });

  /**
   * configs + data
   */
  const [cardConfigs, setCardConfigs] = React.useState<any>(null);
  const [rawCardData, setRawCardData] = React.useState<any[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<BasicPivotItem[]>([]);

  const configLoaded = cardConfigs != null;
  const dataLoaded = rawCardData.length > 0;

  /**
   * On editor mount fetch configs first, then data
   */
  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const configs = await getCardDefns(modalParams.cardViewDefns);
        if (cancelled) return;

        setCardConfigs(configs);

        // Only fetch data once configs are ready
        const raw = await fetchCardData(configs, getTopMembers(data, props.modalParams));
        if (cancelled) return;

        setRawCardData(raw);
      } catch (error) {
        const message = 'Loading similar items has failed';
        toast.error(message);
        ServiceContainer.loggingService.error(message, errorToLoggingPayload(error));
      }
    };

    load();

    // Prevent state updates if editor unmounts mid-request
    return () => {
      cancelled = true;
    };
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Derived card data (grouped, filtered, sorted)
   */
  const formattedCardData = React.useMemo(() => {
    if (!cardConfigs) return [];
    return getGroupedData(rawCardData, cardConfigs as CanvasViewSlice, subheaderSlice, selectedItems);
  }, [rawCardData, cardConfigs, subheaderSlice, selectedItems]);

  /**
   * Cancel = close modal + exit grid editing safely
   */
  const onCancel = () => {
    setOpen(false);
    api.stopEditing(true);
  };

  /**
   * take selected item, map to updates, apply updates to the row, then exit editing
   */
  const onSubmit = () => {
    try {
      if (!node) {
        api.stopEditing(true);
        return;
      }

      if (!selectedItems?.length) {
        // no selection
        api.stopEditing(true);
        return;
      }

      const picked = selectedItems[0];

      forEach(modalParams.updates, ({ from, to }) => {
        const newValue = get(picked, from);
        console.log(to, newValue);
        node.setDataValue(to, newValue);
      });

      api.refreshCells({ rowNodes: [node] });

      setOpen(false);
      api.stopEditing();
    } catch (error) {
      const message = 'Updating row with similar item attributes has failed';
      toast.error(message);
      ServiceContainer.loggingService.error(message, errorToLoggingPayload(error));
    }
  };

  /**
   * subheader changes (search, groupBy, sortBy, etc.)
   */
  const onSubheaderChange = (changes: ViewSimiliarItemsSubheaderChange) => {
    setSubheaderSlice((prev) => applySubheaderChanges(prev, changes));
  };

  /**
   * card toggle:
   * - finds the real pivot item using identity config
   * - enforces single-select (because this would be for 'swapping' an item)
   */
  const onToggleItem = (item: any) => {
    if (!cardConfigs) return;

    const identityPropsConfig = cardConfigs.identityPropsConfig;
    const toggledItem = rawCardData.find((d) => item.id === d[identityPropsConfig.id]);

    if (toggledItem != null) {
      setSelectedItems([toggledItem]);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        style: { overflow: 'visible', width: viewWidth(95) },
      }}
      transitionDuration={0}
      disablePortal={true}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown') {
          onCancel();
        }
      }}
    >
      <DialogTitle classes={{ root: modalStyles.modalTitle }} disableTypography={true}>
        <div className={modalStyles.titleContainer}>
          <div className={classes(modalStyles.leftTitleContainer, modalStyles.modalTitleText)}>
            {modalParams.buttonText ?? 'View Similar Items'}
          </div>
          <div className={classes(modalStyles.rightTitleContainer, modalStyles.modalTitleText)}>
            <i className={classes('far fa-times', modalStyles.modalTitleIcon)} onClick={onCancel} />
          </div>
        </div>
      </DialogTitle>

      <DialogContent classes={{ root: modalStyles.modalContent }}>
        <ViewSimilarItemsSubheader
          viewDefns={modalParams.subheaderViewDefns}
          subheaderSlice={subheaderSlice}
          onSubheaderChange={onSubheaderChange}
        />
        <ViewsimilarItemsCardsView
          cardType={modalParams.cardType}
          level={modalParams.level}
          subheaderSlice={subheaderSlice}
          configLoaded={configLoaded}
          dataLoaded={dataLoaded}
          configs={cardConfigs}
          cardData={formattedCardData}
          onToggleItem={onToggleItem}
        />
      </DialogContent>

      <CardActions className={classes(MUIStyles.cardActions, modalStyles.leftAction)}>
        <AcceptButton onClick={onSubmit} />
        <RejectButton onClick={onCancel} />
      </CardActions>
    </Dialog>
  );
}
